import { createContext, createEffect, createSignal, type JSX, onMount, useContext } from 'solid-js'
import { authService } from '~/services/auth.service'
import { authStore } from '~/stores/auth'
import { setCompanyStore } from '~/stores/company'

type AuthContextType = {
  isAuthenticated: () => boolean
  /**
   * true mientras se comprueba contra el servidor si el token guardado sigue
   * valiendo. Los guards deben esperar: si no, con un token caducado se pinta
   * el panel entero durante un instante antes de expulsar.
   */
  checking: () => boolean
  user: () => Record<string, unknown> | null
  token: () => string | null
  login: (email: string, password: string) => Promise<boolean>
  googleLogin: (accessToken: string) => Promise<boolean>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>()

export function AuthProvider(props: { children: JSX.Element }) {
  const [isAuthenticated, setIsAuthenticated] = createSignal<boolean>(!!authStore.token)
  /** Sólo hay que comprobar si venimos con un token guardado de otra sesión. */
  const [checking, setChecking] = createSignal<boolean>(!!authStore.token)

  /**
   * Al arrancar, un token en localStorage NO significa sesión válida: puede
   * estar caducado o haber sido revocado. Se pregunta al servidor una vez.
   *
   * Si no vale, el interceptor de api.ts recibe el 401, limpia la sesión y
   * manda al login; aquí sólo hay que bajar la bandera para que el guard deje
   * de esperar. Sin esta comprobación se entraba al panel con un token muerto
   * y todas las peticiones fallaban en silencio.
   */
  onMount(async () => {
    if (!authStore.token) return
    try {
      await authService.me()
    } catch {
      // El interceptor ya limpió la sesión y redirige; no hay nada que añadir.
    } finally {
      setChecking(false)
    }
  })

  const login = async (email: string, password: string): Promise<boolean> => {
    await authService.login({ email, password })
    setIsAuthenticated(true)
    return true
  }

  const googleLogin = async (idToken: string): Promise<boolean> => {
    await authService.googleLogin(idToken)
    setIsAuthenticated(true)
    return true
  }

  const logout = async () => {
    await authService.logout()
    setCompanyStore({ id: null, name: null, slug: null, logo: null, domain: null })
    setIsAuthenticated(false)
  }

  const refreshUser = async () => {
    try {
      await authService.me()
    } catch {}
  }

  createEffect(() => {
    setIsAuthenticated(!!authStore.token)
  })

  const value: AuthContextType = {
    isAuthenticated,
    checking,
    user: () => authStore.user,
    token: () => authStore.token,
    login,
    googleLogin,
    logout,
    refreshUser
  }

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
